import EditNameModal from '@/components/features/data/EditNameModal'
import { NewTaskButton } from '@/components/features/taskCreate/NewTaskButton'
import { TaskRunDetails } from '@/components/features/tasks/TaskRunDetails'
import { TaskSort, TaskTable } from '@/components/features/tasks/TaskTable'
import Modal from '@/components/layout/Modal'
import TaskCancelConfirmationDialog from '@/components/utils/TaskCancelConfirmationDialog'
import TaskDeletionConfirmationDialog from '@/components/utils/TaskDeletionConfirmationDialog'
import { useClient } from '@/context/ClientContext'
import { useOffsetPaginationQuery } from '@/hooks/useOffsetPaginationQuery'
import { useOrganisationUsersWithUserMap } from '@/hooks/useOrganisationUsersWithUserMap'
import { usePermissionCheck } from '@/hooks/usePermissionCheck'
import { ACCENT_DOT, ACCENT_VALUE, type Accent } from '@/lib/accent'
import { fetchTasksPaginated } from '@/lib/clientHelpers'
import { summaryKeys } from '@/lib/queryKeys'
import { NON_TERMINAL_TASK_STATUSES } from '@/lib/taskStatusDisplay'
import { cn } from '@/lib/utils'
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'

import { PresignedUrlType } from '@droidmap/asset-service-contract'
import { Input } from '@droidmap/frontend-ui'
import { Spinner } from '@droidmap/frontend-ui'
import { isCancelRequestInFlight } from '@droidmap/mapping-job-service-contract'
import { Permission } from '@droidmap/permissions-service-contract'
import {
  DeleteTaskInput,
  Task,
  TaskDeletionScope,
  TaskListSortKey,
  TaskStatus,
  UpdateTaskInput,
} from '@droidmap/project-service-contract'

const POLLING_INTERVAL = 10000
const PREFERENCE_KEY = 'PROJECT_TASKS'

// Map TaskTable column keys to backend list sort columns. The backend
// sorts the `createdBy` ULID directly — same owner's tasks group
// together, but inter-owner order isn't alphabetical. Resolving owner
// names server-side would require a join across services; deferred.
const SORT_KEY_TO_BACKEND: Record<TaskSort['key'], TaskListSortKey> = {
  name: 'name',
  status: 'status',
  type: 'type',
  owner: 'createdBy',
  created: 'created',
}

export const Route = createFileRoute(
  '/_authenticated/orgs/$orgId_/projects/$projectId/tasks',
)({
  loader: ({ context }) => ({
    crumb: 'Tasks',
    metadata: context.metadata,
  }),
  component: ProjectTasksPage,
})

// Per-tone border + count text colour for the count pills. Background and
// dot styles come from the shared `ACCENT_*` palette.
const PILL_BORDER: Record<Accent, string> = {
  neutral: 'border-border',
  cyan: 'border-accent-tint',
  teal: 'border-accent-2/30',
  violet: 'border-violet/30',
  success: 'border-success/30',
  warning: 'border-warning/30',
  danger: 'border-danger/30',
}

function StatusPill({
  count,
  label,
  tone,
}: {
  count: number
  label: string
  tone: Accent
}) {
  // Auto-fade pills with no items so live state pops.
  const effective: Accent = count === 0 ? 'neutral' : tone
  const isLive = count > 0 && (effective === 'cyan' || effective === 'warning')
  return (
    <div
      className={cn(
        'bg-panel inline-flex items-center gap-2 rounded-md border px-3 py-1.5 font-mono text-[11px]',
        PILL_BORDER[effective],
      )}
    >
      <span
        aria-hidden
        className={cn(
          'h-2 w-2 rounded-full',
          ACCENT_DOT[effective],
          isLive && 'animate-pulse',
        )}
      />
      <span
        className={cn('text-[13px] font-semibold', ACCENT_VALUE[effective])}
      >
        {count}
      </span>
      <span className="text-text-mute text-[10px] uppercase tracking-[0.08em]">
        {label}
      </span>
    </div>
  )
}

export default function ProjectTasksPage() {
  const { orgId, projectId } = Route.useParams()
  const { metadata } = Route.useRouteContext()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {
    projectClient,
    organisationClient,
    userClient,
    assetClient,
    mappingJobClient,
  } = useClient()
  const { hasPermission } = usePermissionCheck()
  const canRenameTask = hasPermission(Permission.UPDATE_TASK)
  // Cancelling is a write on the task's project, the same permission the
  // service checks (`POST /mapping-jobs/:id/cancel`).
  const canCancelTask = hasPermission(Permission.UPDATE_TASK)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null)
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null)
  const [taskToCancel, setTaskToCancel] = useState<Task | null>(null)
  // Tasks whose cancel was accepted in this visit, before the task's own
  // `cancelRequestedAt` has caught up — that is copied over by an event, so
  // the refetch straight after the cancel can still lack it. The row falls
  // back to its real status once it is terminal, which is also when an id
  // here stops mattering.
  const [cancellingTaskIds, setCancellingTaskIds] = useState<
    ReadonlySet<string>
  >(() => new Set())
  // Held by id, not by value: the list polls while anything is running, and
  // the open run-details panel has to see the task reach its terminal state
  // (and stop its clock) rather than tick on against a snapshot.
  const [runDetailsTaskId, setRunDetailsTaskId] = useState<string | null>(null)
  const [renameErrorId, setRenameErrorId] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [sort, setSort] = useState<TaskSort>({ key: 'created', dir: 'desc' })

  const trimmedFilter = filter.trim()
  const tasksQueryKey = useMemo(
    () => ['tasks', projectId] as const,
    [projectId],
  )

  const { userMap } = useOrganisationUsersWithUserMap({
    orgId,
    organisationClient,
    userClient,
    metadata,
  })

  const {
    data: tasks,
    isLoading: isLoadingTasks,
    pagination,
    refresh: refreshPagination,
    refetch,
  } = useOffsetPaginationQuery<Task>({
    preferenceKey: PREFERENCE_KEY,
    queryKey: tasksQueryKey,
    queryFn: ({ page, pageSize }) =>
      fetchTasksPaginated(projectId, projectClient, metadata, {
        page,
        pageSize,
        filter: trimmedFilter || undefined,
        sortBy: SORT_KEY_TO_BACKEND[sort.key],
        sortDir: sort.dir,
      }),
    resetKey: {
      filter: trimmedFilter,
      sortKey: sort.key,
      sortDir: sort.dir,
    },
    // Conditional polling: only poll if there are active tasks on the
    // current page. Status filter isn't pushed to the server today, so
    // this approximates "any pending/running tasks exist" by checking
    // the current page only.
    refetchInterval: (query) => {
      const taskItems = query.state.data?.items
      if (!taskItems) return false
      return taskItems.some(
        (task) =>
          task.status === TaskStatus.PENDING ||
          task.status === TaskStatus.RUNNING,
      )
        ? POLLING_INTERVAL
        : false
    },
    refetchIntervalInBackground: false,
  })

  // Status pill counts reflect the current page only — pushing per-status
  // counts to the server is a follow-up; the underlying filter lives
  // server-side already so we'd just need a `statusCounts` field on the
  // tasks list response.
  const summary = useMemo(() => {
    return {
      complete: tasks.filter((t) => t.status === TaskStatus.COMPLETED).length,
      running: tasks.filter((t) => t.status === TaskStatus.RUNNING).length,
      queued: tasks.filter((t) => t.status === TaskStatus.PENDING).length,
      failed: tasks.filter(
        (t) =>
          t.status === TaskStatus.FAILED || t.status === TaskStatus.CANCELLED,
      ).length,
    }
  }, [tasks])

  const updateTaskMutation = useMutation({
    mutationFn: async (input: UpdateTaskInput) =>
      projectClient.updateTask(input, metadata),
    onMutate: () => {
      setRenameErrorId(null)
    },
    onSuccess: async () => {
      setTaskToEdit(null)
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: tasksQueryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: summaryKeys.tasks(projectId),
        }),
      ])
    },
    onError: (_err, { taskId }) => {
      setRenameErrorId(taskId)
    },
  })

  const deleteTaskMutation = useMutation({
    mutationFn: async (input: DeleteTaskInput) => {
      await projectClient.deleteTask(
        {
          taskId: input.taskId,
          deletionScope: input.deletionScope as TaskDeletionScope,
        },
        { ...metadata },
      )
    },
    onSuccess: async () => {
      refreshPagination()
      await queryClient.invalidateQueries({
        queryKey: summaryKeys.tasks(projectId),
      })
      setDeleteModalOpen(false)
      setTaskToDelete(null)
    },
  })

  const cancelTaskMutation = useMutation({
    mutationFn: async (task: Task) => {
      if (!task.referenceId) throw new Error('This task has no job to cancel')
      return mappingJobClient.cancelMappingJob(
        { mappingJobId: task.referenceId },
        metadata,
      )
    },
    onSuccess: async (_job, task) => {
      setCancellingTaskIds((ids) => new Set(ids).add(task.id))
      setTaskToCancel(null)
      await queryClient.invalidateQueries({ queryKey: tasksQueryKey })
    },
  })

  // Only tasks still on their way to a terminal status read "Cancelling…".
  // The task's recorded request is what carries this across a reload; one
  // that ran its course without stopping the job lapses, so the action is
  // offered again rather than hidden for good.
  const activeCancellingTaskIds = useMemo(
    () =>
      new Set(
        tasks
          .filter(
            (task) =>
              NON_TERMINAL_TASK_STATUSES.includes(task.status) &&
              (cancellingTaskIds.has(task.id) ||
                isCancelRequestInFlight(task.cancelRequestedAt)),
          )
          .map((task) => task.id),
      ),
    [tasks, cancellingTaskIds],
  )

  const handleViewTask = useCallback(
    (task: Task) => {
      // If the task has a single ortho/map output, jump straight to it.
      const firstOutput = task.outputAssetSetReferences?.[0]
      if (firstOutput) {
        navigate({
          to: `/orgs/${orgId}/projects/${projectId}/asset-sets/${firstOutput.id}`,
        })
      }
    },
    [navigate, orgId, projectId],
  )

  const handleDownloadTask = useCallback(
    async (task: Task) => {
      // Download the first output asset set's first PDF/report if present.
      const firstAsset = task.outputAssetSetReferences?.[0]?.viewableAssets?.[0]
      if (firstAsset) {
        try {
          const asset = await assetClient.getAsset(
            { assetId: firstAsset.id, presignedUrlType: PresignedUrlType.GET },
            metadata,
          )
          if ('presignedUrl' in asset && asset.presignedUrl) {
            window.open(asset.presignedUrl, '_blank')
          }
        } catch (error) {
          console.error('Failed to open task output:', error)
        }
      }
    },
    [assetClient, metadata],
  )

  const confirmDelete = (deletionScope: TaskDeletionScope) => {
    if (taskToDelete) {
      deleteTaskMutation.mutate({
        taskId: taskToDelete.id,
        deletionScope: deletionScope as TaskDeletionScope,
      })
    }
  }

  const handleDeleteTask = (task: Task) => {
    setTaskToDelete(task)
    setDeleteModalOpen(true)
  }

  const handleRefresh = () => {
    void refetch()
  }

  const runDetailsTask =
    tasks.find((task) => task.id === runDetailsTaskId) ?? null

  return (
    <>
      {/* Toolbar */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="w-72 max-w-full">
            <Input
              id="task-filter"
              name="filter"
              type="text"
              placeholder="Filter tasks…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              leadingIcon={<MagnifyingGlassIcon className="h-3.5 w-3.5" />}
              className="h-10"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill
              count={summary.complete}
              label="Complete"
              tone="success"
            />
            <StatusPill
              count={summary.running}
              label="Running"
              tone="warning"
            />
            <StatusPill count={summary.queued} label="Queued" tone="neutral" />
            <StatusPill count={summary.failed} label="Failed" tone="danger" />
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={handleRefresh}
            title="Refresh"
            className={cn(
              'border-border-strong bg-panel text-text-dim hover:text-text hover:border-accent-tint grid h-9 w-9 place-items-center rounded-md border transition-colors duration-150',
            )}
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
          <NewTaskButton orgId={orgId} projectId={projectId} />
        </div>
      </div>

      {isLoadingTasks && tasks.length === 0 ? (
        <div className="flex justify-center py-16">
          <Spinner size="page" />
        </div>
      ) : tasks.length === 0 ? (
        <div className="border-border bg-panel rounded-lg border p-12 text-center">
          <p className="text-text-dim">
            {trimmedFilter
              ? 'No tasks match the current filter.'
              : 'No tasks in this project yet.'}
          </p>
        </div>
      ) : (
        <TaskTable
          tasks={tasks}
          userMap={userMap}
          onView={handleViewTask}
          onDownload={handleDownloadTask}
          onDelete={handleDeleteTask}
          onEdit={canRenameTask ? setTaskToEdit : undefined}
          onViewRunDetails={(task) => setRunDetailsTaskId(task.id)}
          onCancel={
            canCancelTask
              ? (task) => {
                  cancelTaskMutation.reset()
                  setTaskToCancel(task)
                }
              : undefined
          }
          cancellingTaskIds={activeCancellingTaskIds}
          sort={sort}
          onSortChange={setSort}
          pagination={pagination}
        />
      )}

      <Modal
        title={runDetailsTask ? `${runDetailsTask.name} · run details` : ''}
        description="How long this run has taken, set against previous runs of the same project."
        icon={<PresentationChartLineIcon />}
        isOpen={runDetailsTask !== null}
        onClose={() => setRunDetailsTaskId(null)}
        maxWidth="sm:max-w-3xl"
      >
        {runDetailsTask && (
          <TaskRunDetails
            task={runDetailsTask}
            metadata={metadata}
            className="mt-5"
          />
        )}
      </Modal>

      <EditNameModal
        isOpen={taskToEdit !== null}
        onClose={() => {
          setTaskToEdit(null)
          setRenameErrorId(null)
        }}
        entityType="Task"
        initialName={taskToEdit?.name ?? ''}
        isPending={updateTaskMutation.isPending}
        error={
          updateTaskMutation.isError && renameErrorId === taskToEdit?.id
            ? updateTaskMutation.error
            : null
        }
        onSubmit={(newName) => {
          if (!taskToEdit) return
          updateTaskMutation.mutate({ taskId: taskToEdit.id, name: newName })
        }}
      />

      <TaskCancelConfirmationDialog
        isOpen={taskToCancel !== null}
        onClose={() => setTaskToCancel(null)}
        onConfirm={() => {
          if (taskToCancel) cancelTaskMutation.mutate(taskToCancel)
        }}
        task={taskToCancel}
        isLoading={cancelTaskMutation.isPending}
        error={cancelTaskMutation.error}
      />

      <TaskDeletionConfirmationDialog
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false)
          setTaskToDelete(null)
        }}
        onConfirm={confirmDelete}
        task={taskToDelete}
        isLoading={deleteTaskMutation.isPending}
      />
    </>
  )
}
